// Credit Terminal — owner-facing API client for the Accounting Admin Website.
// Talks only to the new /api/CreditTerminal/* endpoints (isolated from the existing Tranzila stack).

import config from '../config';
import selectedEnvironment from '../env';

const BASE = config[selectedEnvironment].API_URL;
const ROOT = `${BASE}/CreditTerminal`;
const CLIENT_ROOT = `${BASE}/ClientPaysAccountant`;
// const CLIENT_ROOT = `${BASE}/ClientPaysAccountant`;

const getCurrentToken = async () => {
    const raw = localStorage.getItem('token');
    return raw ? JSON.parse(raw) : null;
};
const jsonHeaders = async () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${await getCurrentToken()}`,
});
const guard = async (res) => {
    if (!res.ok) {
        const txt = await res.text();
        let msg;
        try { msg = JSON.parse(txt).message || JSON.parse(txt).error || txt; }
        catch { msg = txt; }
        throw new Error(msg);
    }
    const ct = res.headers.get('content-type');
    if (ct?.includes('application/json')) return res.json();
    return null;
};

const get = async (url) => guard(await fetch(url, { headers: await jsonHeaders() }));
const post = async (url, body) => guard(await fetch(url, { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(body) }));
const del = async (url) => guard(await fetch(url, { method: 'DELETE', headers: await jsonHeaders() }));

const newIdempotencyKey = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
/** Who receives the payment when a client creates a link (POST /payment-requests). */
export const CreditTerminalPayeeTargets = {
    customer: 'customer',
    accountant: 'accountant',
};

const creditTerminalService = {
    newIdempotencyKey,
    PayeeTargets: CreditTerminalPayeeTargets,
    newIdempotencyKey,

    // ─── status / request-service ───
    status: () => get(`${ROOT}/status`),
    requestService: () => post(`${ROOT}/request-service`, {}),

    // ─── charge / refund / transactions ───
    charge: (dto) => post(`${ROOT}/charge`, dto),
    // Grow hosted-page flow for new cards: returns { paymentRequestId, callbackToken, url }.
    chargeHosted: (dto) => post(`${ROOT}/charge/hosted`, dto),
    refund: (transactionId, dto) => post(`${ROOT}/transactions/${transactionId}/refund`, dto),
    listTransactions: (params = {}) => {
        const q = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== '') q.set(k, v);
        });
        return get(`${ROOT}/transactions?${q.toString()}`);
    },
    getTransaction: (id) => get(`${ROOT}/transactions/${id}`),

    // ─── customers ───
    upsertCustomer: (dto) => post(`${ROOT}/customers`, dto),
    deleteCustomer: (customerId) => del(`${ROOT}/customers/${customerId}`),
    listCustomers: ({ search, page = 1, pageSize = 50 } = {}) => {
        const q = new URLSearchParams();
        if (search) q.set('search', search);
        q.set('page', page);
        q.set('pageSize', pageSize);
        return get(`${ROOT}/customers?${q.toString()}`);
    },
    deleteCustomer: (id) => del(`${ROOT}/customers/${id}`),

    // ─── saved cards ───
    listSavedCards: (customerId) => get(`${ROOT}/saved-cards${customerId ? `?customerId=${customerId}` : ''}`),
    deleteSavedCard: (id) => del(`${ROOT}/saved-cards/${id}`),

    // ─── payment requests (links) ───
    createPaymentRequest: (dto) => post(`${ROOT}/payment-requests`, dto),

    // ─── client pays accountant (Use Case B) ───
    clientPays: {
        myAccountant: () => get(`${CLIENT_ROOT}/my-accountant`),
        session: (dto) => post(`${CLIENT_ROOT}/session`, dto),
        // Poll until the hosted page completes (status: 2=Paid, 3=Failed, 4=Expired).
        sessionStatus: (id) => get(`${CLIENT_ROOT}/session/${id}`),
        listSavedCards: () => get(`${CLIENT_ROOT}/saved-cards`),
        paySaved: (dto) => post(`${CLIENT_ROOT}/pay-saved`, dto),
        deleteSavedCard: (id) => del(`${CLIENT_ROOT}/saved-cards/${id}`),
    },
    getPaymentRequest: (id) => get(`${ROOT}/payment-requests/${id}`),

    // ─── import customers from account cards ───
    importPreview: (caseId) => get(`${ROOT}/customers/import/preview?caseId=${encodeURIComponent(caseId)}`),
    importCustomers: (caseId, externalKeys) =>
        post(`${ROOT}/customers/import`, { caseId, externalKeys: externalKeys || null }),

    // ─── view a generated PaymentAcceptance document (receipt / refund) ───
    getPaymentAcceptanceDocUrl: async (paymentAcceptanceId) => {
        const res = await fetch(`${BASE}/PaymentAcceptances/GetDocumentUrlById`, {
            method: 'POST',
            headers: await jsonHeaders(),
            body: JSON.stringify(paymentAcceptanceId),
        });
        return guard(res);
    },

    // ─── view a generated Income document (refund credit note - חשבונית מס זיכוי) ───
    getIncomeDocUrl: async (incomeId) => {
        const res = await fetch(`${BASE}/Incomes/GetDocumentUrlById`, {
            method: 'POST',
            headers: await jsonHeaders(),
            body: JSON.stringify(incomeId),
        });
        return guard(res);
    },
};

export default creditTerminalService;
